import i18n from 'i18next';
import { getCompanyRoleTranslationKey } from './localization';
import { getTenantCases } from './lib/badgeUtils';

const getIntlLocale = () => i18n.resolvedLanguage === 'sv' ? 'sv-SE' : 'en-SE';

const getCompanyRole = (value: string) => {
  const translationKey = getCompanyRoleTranslationKey(value);
  return translationKey && i18n.exists(translationKey) ? i18n.t(translationKey) : value;
};

export const htmlTitle = (html: string): HTMLElement => {
    const container = document.createElement("div");
    container.innerHTML = html;
    return container;
  }

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const renderInlineMarkdown = (value: string): string => {
    let rendered = escapeHtml(value);

    rendered = rendered.replace(/`([^`]+)`/g, '<code>$1</code>');
    rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>');

    return rendered;
  };

  export const renderMarkdownToSafeHtml = (markdown: string): string => {
    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
    const blocks: string[] = [];
    let paragraphLines: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let listItems: string[] = [];

    const flushParagraph = () => {
      if (paragraphLines.length === 0) return;
      blocks.push(`<p>${paragraphLines.join('<br />')}</p>`);
      paragraphLines = [];
    };

    const flushList = () => {
      if (!listType || listItems.length === 0) {
        listType = null;
        listItems = [];
        return;
      }

      blocks.push(`<${listType}>${listItems.join('')}</${listType}>`);
      listType = null;
      listItems = [];
    };

    lines.forEach((line) => {
      const trimmed = line.trim();

      if (trimmed.length === 0) {
        flushParagraph();
        flushList();
        return;
      }

      const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
      if (headingMatch) {
        flushParagraph();
        flushList();
        const level = Math.min(headingMatch[1].length, 3);
        blocks.push(`<h${level}>${renderInlineMarkdown(headingMatch[2])}</h${level}>`);
        return;
      }

      const unorderedListMatch = trimmed.match(/^[-*]\s+(.+)$/);
      if (unorderedListMatch) {
        flushParagraph();
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(`<li>${renderInlineMarkdown(unorderedListMatch[1])}</li>`);
        return;
      }

      const orderedListMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (orderedListMatch) {
        flushParagraph();
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(`<li>${renderInlineMarkdown(orderedListMatch[1])}</li>`);
        return;
      }

      flushList();
      paragraphLines.push(renderInlineMarkdown(trimmed));
    });

    flushParagraph();
    flushList();

    return blocks.join('');
  };

  const formatCommentTimestamp = (updatedAt?: string): string | null => {
    if (!updatedAt) {
      return null;
    }

    const parsed = new Date(updatedAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toLocaleString(getIntlLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  export const generateCompanyCommentHTML = (commentMarkdown?: string, updatedAt?: string): string => {
    if (!commentMarkdown || !commentMarkdown.trim()) {
      return '';
    }

    const formattedTimestamp = formatCommentTimestamp(updatedAt);
    const metaHtml = formattedTimestamp
      ? `<div class="graph-comment-meta">${escapeHtml(i18n.t('common.fields.updated'))} ${escapeHtml(formattedTimestamp)}</div>`
      : '';

    return `
      <section class="graph-comment-card">
        <div class="graph-comment-header">
          <span class="graph-comment-badge">C</span>
          <div>
            <div class="graph-comment-title">${escapeHtml(i18n.t('graph.tenantComment'))}</div>
            ${metaHtml}
          </div>
        </div>
        <div class="graph-comment-markdown">${renderMarkdownToSafeHtml(commentMarkdown)}</div>
      </section>`;
  };

  export const generateTenantCasesHTML = (
    properties: Record<string, unknown> | null | undefined,
  ): string => {
    const tenantCases = getTenantCases(properties);
    if (tenantCases.length === 0) return '';
    const translate = (key: string, fallback: string) => {
      const translated = i18n.t(key, { defaultValue: fallback });
      return typeof translated === 'string' && translated ? translated : fallback;
    };

    const rows = tenantCases.map((tenantCase) => {
      const parsedDate = new Date(tenantCase.case_date);
      const formattedDate = Number.isNaN(parsedDate.getTime())
        ? tenantCase.case_date
        : parsedDate.toLocaleDateString(getIntlLocale(), {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
      const statusKey = `graph.caseStatuses.${tenantCase.case_status.toLowerCase()}`;
      const status = i18n.exists(statusKey)
        ? translate(statusKey, tenantCase.case_status)
        : tenantCase.case_status;

      return `<tr>
        <td class="table-cell">${escapeHtml(tenantCase.case_number)}</td>
        <td class="table-cell">${escapeHtml(formattedDate)}</td>
        <td class="table-cell">${escapeHtml(status)}</td>
      </tr>`;
    }).join('');

    return `
      <table class="styled-table tenant-case-table">
        <thead>
          <tr><th class="table-header" colspan="3">${escapeHtml(translate('graph.directTenantCases', 'Directly linked to a case'))}</th></tr>
          <tr>
            <th class="table-header">${escapeHtml(translate('graph.caseNumber', 'Case number'))}</th>
            <th class="table-header">${escapeHtml(translate('graph.caseDate', 'Case date'))}</th>
            <th class="table-header">${escapeHtml(translate('graph.caseStatus', 'Status'))}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
  };
  
  export const parseJSONSafely = (jsonString: string): any[] => {
    try {
      // Replace single quotes with double quotes and None with null
      const formattedString = jsonString
        .replace(/'/g, '"') // Replace single quotes with double quotes
        .replace(/None/g, 'null') // Replace None with null
        .replace(/True/g, 'true') // Replace True with true
        .replace(/False/g, 'false'); // Replace False with false
  
      // Parse the now-correctly formatted JSON string
      return JSON.parse(formattedString);
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      return [];
    }
  };
  
  export const generatePropertiesTableFromJSON = (jsonArray: any[]): string => {
    if (jsonArray.length === 0) return '';

    const columnMap: { [key: string]: string } = {
      'funktionar_funk_text': i18n.t('assessment.details.role'),
      'start_date': i18n.t('common.fields.startDate'),
      'end_date': i18n.t('common.fields.endDate')
    };

    // Determine columns to include based on the first item in the array
    const columnsToInclude = ['start_date', 'end_date'];
    if (jsonArray[0] && 'funktionar_funk_text' in jsonArray[0]) {
      columnsToInclude.unshift('funktionar_funk_text');
    }

    const headers = columnsToInclude.map(column => columnMap[column]);

    const tableRows = jsonArray.map((item) => {
      const rowCells = columnsToInclude.map(column => {
        const rawValue = item[column];
        let displayValue = i18n.t('common.states.notAvailable');
        if (rawValue !== null && rawValue !== undefined) {
          if (column === 'funktionar_funk_text') {
            displayValue = getCompanyRole(String(rawValue));
          } else if (column === 'start_date' || column === 'end_date') {
            const date = new Date(String(rawValue));
            displayValue = Number.isNaN(date.getTime())
              ? String(rawValue)
              : date.toLocaleDateString(getIntlLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
          } else {
            displayValue = String(rawValue);
          }
        }
        return `<td class="table-cell">${escapeHtml(displayValue)}</td>`;
      }).join('');
      return `<tr>${rowCells}</tr>`;
    }).join('');
    const tableHeaders = headers.map(header =>
      `<th class="table-header">${header}</th>`
    ).join('');
    return `
      <table class="styled-table">
        <thead>
          <tr>${tableHeaders}</tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>`;
  };

  export const generateCategoryTableFromJSON = (jsonArray: any[]): string => {
    if (Object.keys(jsonArray).length === 0) return '';

    const headers = [i18n.t('assessment.debt.category'), i18n.t('assessment.debt.amount'), i18n.t('assessment.details.count')];

    const tableRows = Object.entries(jsonArray).map(([category, values]) => {
        const rowCells = `
            <td class="table-cell">${escapeHtml(category)}</td>
            <td class="table-cell">${values.skuldsaldo_current.toLocaleString(getIntlLocale())}</td>
            <td class="table-cell">${values.antal_current.toLocaleString(getIntlLocale())}</td>
        `;
        return `<tr>${rowCells}</tr>`;
    }).join('');

    const tableHeaders = headers.map(header =>
        `<th class="table-header">${header}</th>`
    ).join('');

    return `
        <table class="styled-table">
            <thead>
                <tr>${tableHeaders}</tr>
            </thead>
            <tbody>${tableRows}</tbody>
        </table>`;
};

export const generateAggregationDataHTML = (aggData: any, isStartNode: boolean = false, isExpanded: boolean = false): string => {
    if (isStartNode || !aggData || isExpanded) return '';
    
    const aggregationItems: string[] = [];
    
    // Note: Counts are now displayed as badges on the node itself, so we don't show them in the hover tooltip anymore.
    /*
    if (aggData.next_layer_company_count !== undefined && aggData.next_layer_company_count !== 0) {
        aggregationItems.push(`🔗 Additional company connections: ${aggData.next_layer_company_count}`);
    }
    
    if (aggData.next_layer_active_debt_count !== undefined && aggData.next_layer_active_debt_count !== 0) {
        aggregationItems.push(`⚠️ Additional company connections with KFM debt: ${aggData.next_layer_active_debt_count}`);
    }
    */
    
    if (aggregationItems.length === 0) return '';
    
    const aggregationRows = aggregationItems.map(item => 
        `<tr><td class="table-cell" colspan="3">${item}</td></tr>`
    ).join('');
    
    return `
        <table class="styled-table">
            <thead>
                <tr><th class="table-header" colspan="3">${escapeHtml(i18n.t('graph.hiddenConnections'))}</th></tr>
            </thead>
            <tbody>${aggregationRows}</tbody>
        </table>`;
};
